import moment from 'moment'

type ProgressMessage = { value: number, max: number } | { progress_value: number, progress_max: number }

/**
 * Use in conjunction with ActionCable and our convention of sending progress updates on long-running processes
 * @example progress = {progress_value: 7, progress_max: 10, mode: 'some string'}
 */
export default class Progress {
  endTime: number = null
  max: number = 0
  startTime: number = null
  value: number = 0

  /**
   * @return [number] in milliseconds, rounded up to nearest second
   */
  get eta(): number {
    if (!this.startTime) return null

    const elapsedMS = Date.now() - this.startTime
    const msPerValue = elapsedMS / this.value
    const valueRemaining = this.max - this.value
    return Math.ceil(msPerValue * valueRemaining / 1000) * 1000
  }

  get isProgressing(): boolean {
    return Boolean(this.startTime) && !Boolean(this.endTime)
  }

  /**
   * @return [number] 0 - 100 assuming valid +value+ and +max+ being 0 or positive with +value+ < +max+
   */
  get percent(): number {
    if (!this.max) return 0

    return this.value / this.max * 100
  }

  formatETA(format: string): string {
    if (!this.eta) return ''
    return moment.utc(this.eta).format(format)
  }

  formatPercent({ decimal }: { decimal?: number }): string {
    decimal = decimal ?? 0
    return this.percent.toFixed(decimal)
  }

  start(progress: ProgressMessage): void {
    this.startTime = null
    this.endTime = null
    this.update(progress)
  }

  stop(): void {
    this.endTime = Date.now()
  }

  update(progress: ProgressMessage): void {
    if (!this.startTime) this.startTime = Date.now()

    if ('value' in progress) {
      this.value = progress.value ?? 0
      this.max = progress.max ?? 0
    } else {
      this.value = progress.progress_value ?? 0
      this.max = progress.progress_max ?? 0
    }
  }
}
